import type { RenderableTreeNode, RenderableTreeNodes, Tag } from "@ext/markdown/core/render/logic/Markdoc";
import getChildTextId from "@ext/markdown/elements/heading/logic/getChildTextId";
import headingTransformer from "@ext/markdown/elements/heading/render/logic/headingTransformer";
import HtmlTagComponentEditTreeToRenderTree from "@ext/markdown/elements/htmlTag/render/logic/HtmlTagComponentEditTreeToRenderTreeTransformer";
import type { JSONContent } from "@tiptap/core";
import type { Schema } from "@tiptap/pm/model";

type ComponentsNames = Record<string, string>;
export type Child = JSONContent | Tag;
type Attrs = Record<string, unknown>;

export type CustomEditTreeToRenderTree = (node: JSONContent) => RenderableTreeNodes;
export type EditTreeToRenderTreeTransformer = (node: JSONContent) => JSONContent;

const getComponentNames = (): ComponentsNames => {
	return {
		doc: "article",
		text: "Text",
		heading: "Heading",
		paragraph: "p",
		strong: "strong",
		em: "em",
		link: "Link",
		s: "s",
		code: "Code",
		image: "Image",
		cut: "Cut",
		cmd: "Cmd",
		who: "Who",
		kbd: "Kbd",
		include: "Include",
		formula: "Formula",
		video: "Video",
		alfa: "Alfa",
		beta: "Beta",
		when: "When",
		horizontal_rule: "hr",
		tbody: "tbody",
		color: "Color",
		alert: "Alert",
		hard_break: "br",
		file: "Link",
		"fragment-link": "Fragment-link",
	};
};

const forceChildren = ["inline-property"];

const getNodeText = (node: JSONContent): string =>
	node.text ?? (node.content ?? []).map((child) => getNodeText(child)).join("");

const diagramsTransformer = (node: JSONContent, componentsNames: ComponentsNames): JSONContent => {
	const diagramName = node.attrs?.diagramName?.toLowerCase();
	if (!diagramName) return node;

	const tagName = componentsNames[diagramName];
	if (!tagName) return node;

	const tag = {
		...node,
		type: tagName.toLowerCase(),
	};

	return tag;
};

const noteTitleCollapseTransformer = (node: JSONContent): JSONContent => {
	if (node.type !== "note") return node;
	const first = node.content?.[0];
	if (first?.type !== "noteTitle") return node;
	const title = (first.content ?? []).map((child) => child.text ?? "").join("");
	return { ...node, attrs: { ...(node.attrs ?? {}), title }, content: node.content.slice(1) };
};

// Replaces soft_break with the space it renders as and merges it into neighbour
// text nodes with the same marks, so the render tree stays exactly the same as
// when softbreak was turned into a space at parse time.
const softBreakTransformer = (node: JSONContent): JSONContent => {
	if (!node.content?.some((child) => child.type === "soft_break")) return node;

	const marksKey = (child: JSONContent) => JSON.stringify(child.marks ?? []);
	const content: JSONContent[] = [];
	for (const child of node.content) {
		const current = child.type === "soft_break" ? { type: "text", text: " ", marks: child.marks } : child;
		const last = content[content.length - 1];
		if (last?.type === "text" && current.type === "text" && marksKey(last) === marksKey(current)) {
			content[content.length - 1] = { ...last, text: last.text + current.text };
		} else {
			content.push(current);
		}
	}

	return { ...node, content };
};

const listTransformer = (node: JSONContent): JSONContent => {
	if (node.type !== "bulletList" && node.type !== "orderedList" && node.type !== "taskList") return node;

	const recursiveAddDepth = (node: JSONContent, depth: number) => {
		if (node.attrs?.depth !== undefined) {
			return node;
		}

		if (node.type !== "bulletList" && node.type !== "orderedList" && node.type !== "taskList") {
			if (node.content) {
				node.content = node.content.map((child) =>
					recursiveAddDepth(
						child,
						child.type === "bulletList" || child.type === "orderedList" || child.type === "taskList"
							? depth + 1
							: depth,
					),
				);
			}

			return node;
		}

		const newNode = {
			...node,
			attrs: {
				...node.attrs,
				depth,
			},
		};

		if (node.content) {
			newNode.content = node.content.map((child) =>
				recursiveAddDepth(
					child,
					child.type === "bulletList" || child.type === "orderedList" || child.type === "taskList"
						? depth + 1
						: depth,
				),
			);
		}

		return newNode;
	};

	return recursiveAddDepth(node, 0);
};

const parentLinksTagTransformer = (tag: Child): object | object[] => {
	// A child of a paragraph is a tag or a piece of text, and text has no children to merge.
	if (typeof tag !== "object" || tag === null) return tag;

	const children = "children" in tag ? tag.children : tag.content;
	if (!Array.isArray(children)) return tag;

	const linkChildrenExist = children.some((child: Tag) => child?.name === "Link");

	if (!linkChildrenExist) return tag;

	const newChildren: (Tag | JSONContent)[] = [];

	for (let i = 0; i < children.length; i++) {
		const child = children[i];

		if (i === 0 || child.name !== "Link") {
			newChildren.push(child);
			continue;
		}

		const currentHref = child.attributes.href;
		const previousElement = newChildren[newChildren.length - 1];

		if (previousElement && previousElement.attributes?.href === currentHref) {
			if ("children" in previousElement) previousElement.children.push(...child.children);
			else if ("content" in previousElement) previousElement.content.push(...child.children);
		} else {
			newChildren.push(child);
		}
	}

	if ("content" in tag) {
		return { ...tag, content: newChildren };
	}

	return {
		...tag,
		children: newChildren,
	};
};

const editTreeToRenderTree = (editTree: JSONContent, editSchema: Schema): RenderableTreeNode => {
	const usedHeadingIds = new Set<string>();

	const transformNode = (node: JSONContent) => {
		if (node.type === "heading") {
			const baseId = node.attrs?.id ?? getChildTextId(getNodeText(node));
			if (node.attrs?.isCustomId) {
				usedHeadingIds.add(baseId);
				return;
			}

			let id = baseId;
			let suffix = 1;

			while (usedHeadingIds.has(id)) {
				id = `${baseId}-${suffix}`;
				suffix++;
			}
			usedHeadingIds.add(id);
			node.attrs = { ...node.attrs, id };
		}

		const nodeHandlers = [
			softBreakTransformer,
			diagramsTransformer,
			listTransformer,
			HtmlTagComponentEditTreeToRenderTree,
			noteTitleCollapseTransformer,
		];

		for (const handler of nodeHandlers) {
			const result = handler(node, componentsNames);
			if (result && node !== result) return result;
		}
	};

	const transformTag = (tag: Child): object | object[] => {
		const tagHandlers = [parentLinksTagTransformer, headingTransformer];

		for (const handler of tagHandlers) {
			const result = handler(tag);
			if (result && tag !== result) return result;
		}

		return tag;
	};

	const createTag = (name: string, attrs: Attrs, children: string | JSONContent | Tag[]): Tag => {
		return {
			// biome-ignore lint/style/useNamingConvention: markdoc Tag protocol field
			$$mdtype: "Tag",
			name,
			attributes: attrs || {},
			children: (Array.isArray(children) ? children : [children]) as Tag[], // Array of Tag and JSONContent
		};
	};

	const createNodeOrMark = (type: string, attrs: Attrs, children: string | JSONContent | Tag[]): JSONContent => {
		return {
			type,
			attrs: attrs || {},
			content: (Array.isArray(children) ? children : [children]) as unknown[], // Array of Tag and JSONContent
		};
	};

	const convertNode = (inputNode: JSONContent): object[] | object | string => {
		const node = transformNode(inputNode) || inputNode;

		if (node?.text && !node?.marks) return node.text;

		const tagName = componentsNames[node.type];
		const isInline = editSchema.nodes[node.content?.[0]?.type]?.isInline;
		const isForceChildren = forceChildren.includes(node.content?.[0]?.type);
		const tag: Child = tagName
			? createTag(tagName, node.attrs || {}, [])
			: createNodeOrMark(node.type, node.attrs || {}, []);

		// Content
		if (node.content) {
			// If the node has only one child and it's an inline node, convert it to a render node
			if (node.content.length === 1 && isInline && !isForceChildren && node.content[0].type !== "text") {
				const convertedNode = convertNode(node.content[0]);
				return convertedNode;
			}

			node.content.forEach((child) => {
				const children = convertNode(child);

				if (Array.isArray(children) && !children.some((child) => child.name === "Text")) {
					children.forEach((child2) => {
						if ("children" in tag) {
							tag.children.push(child2);
						} else if ("content" in tag) {
							tag.content.push(child2);
						}
					});
				} else {
					if ("children" in tag) {
						tag.children.push(children);
					} else if ("content" in tag) {
						tag.content.push(children as JSONContent);
					}
				}
			});
		}

		// Marks
		if (node.marks && node.marks.length > 0) {
			const marks = node.marks.map((mark) => editSchema.marks[mark.type]);
			marks.sort((a, b) => ((b as { rank?: number }).rank || 0) - ((a as { rank?: number }).rank || 0));

			let wrappedTag = node.text ? node.text : tag;
			marks.forEach((mark) => {
				const nodeMark = node.marks.find((m) => m.type === mark.name);
				const markName = componentsNames[mark.name];

				wrappedTag = markName
					? createTag(markName, nodeMark?.attrs || {}, [wrappedTag])
					: createNodeOrMark(mark.name, nodeMark?.attrs || {}, [wrappedTag]);
			});

			return transformTag(wrappedTag as Child);
		}

		return transformTag(tag);
	};

	const componentsNames = getComponentNames();
	const document = convertNode(editTree) as RenderableTreeNode;

	return document;
};

export default editTreeToRenderTree;
